/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import router from "@/router";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import useGetRefreshToken from "@/use/useGetRefreshToken";
import useRemoveTokens from "@/use/useRemoveTokens";
import useSetTokens from "@/use/useSetTokens";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import dayjs from "dayjs";
import { injectable } from "inversify";
import { Result, err } from "neverthrow";
import qs from "qs";
import { MEMBER_ID, RouterName, SERVICE_UID } from "./../../types/index";
import { HttpError } from "./errors/httpError";
import { ParseError } from "./errors/parseError";

type IHttpRequest = {
  url: string;
  config?: AxiosRequestConfig;
  data?: any;
};

// Result로 랩핑된 Parser
type ResultParser<TResponse, TModel> = (
  _: TResponse
) => Result<TModel, ParseError>;

type Parser<TResponse, TModel> = {
  parseTo: ResultParser<TResponse, TModel>;
};

export interface IHttpService {
  get<TResponse, TModel>(
    request: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>>;

  post<TResponse, TModel>(
    request: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>>;

  put<TResponse, TModel>(
    request: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>>;

  delete<TResponse, TModel>(
    request: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>>;

  initService(baseUrl: string): void;
}

export interface IAxiosCreator {
  createAxiosInstance(baseUrl: string): AxiosInstance;
}

export class AxiosCreator implements IAxiosCreator {
  createAxiosInstance(baseUrl: string): AxiosInstance {
    return axios.create({
      baseURL: baseUrl,
      headers: { "Content-Type": "application/json" },
    });
  }
}

@injectable()
export class HttpService implements IHttpService {
  private axiosService: AxiosInstance;
  private axiosCreator = new AxiosCreator();
  private retryTokenRefreshCount = 0;
  private readonly retryTokenRefreshLimit = 1;

  constructor() {
    this.axiosService = axios.create();
  }

  initService(baseUrl: string): void {
    this.axiosService = this.axiosCreator.createAxiosInstance(baseUrl);
    this.axiosService.defaults.timeout = 20000;
    this.axiosService.defaults.timeoutErrorMessage =
      "서버로부터 응답이 없습니다.";
    this._initRequestInterceptor();
    this._initResponseInterceptor();
  }

  public async get<TResponse, TModel>(
    { url, config }: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>> {
    try {
      const response = await this.axiosService.get<TResponse>(url, config);
      return this._parse<TResponse, TModel>(response.data, parser.parseTo);
    } catch (error: any) {
      return err(error);
    }
  }

  public async post<TResponse, TModel>(
    { url, data, config }: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>> {
    try {
      const response = await this.axiosService.post<TResponse>(
        url,
        data,
        config
      );
      return this._parse<TResponse, TModel>(response.data, parser.parseTo);
    } catch (error: any) {
      return err(error);
    }
  }

  public async put<TResponse, TModel>(
    { url, data, config }: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>> {
    try {
      const response = await this.axiosService.put<TResponse>(
        url,
        data,
        config
      );
      return this._parse<TResponse, TModel>(response.data, parser.parseTo);
    } catch (error: any) {
      return err(error);
    }
  }

  public async delete<TResponse, TModel>(
    { url, config }: IHttpRequest,
    parser: Parser<TResponse, TModel>
  ): Promise<Result<TModel, ParseError | HttpError>> {
    try {
      const response = await this.axiosService.delete<TResponse>(url, config);
      return this._parse<TResponse, TModel>(response.data, parser.parseTo);
    } catch (error: any) {
      return err(error);
    }
  }

  private _parse<TResponse, TModel>(
    data: TResponse,
    parser: ResultParser<TResponse, TModel>
  ): Result<TModel, ParseError> {
    try {
      return parser(data);
    } catch (error: any) {
      const parseError = new ParseError(error.message);
      return err(parseError);
    }
  }

  private _initRequestInterceptor() {
    this.axiosService.interceptors.request.use(
      this._handleRequest,
      this._handleError
    );
  }

  private _initResponseInterceptor() {
    this.axiosService.interceptors.response.use(
      (response: AxiosResponse) => response,
      this._handleResponseError
    );
  }

  private _handleRequest(config: InternalAxiosRequestConfig<any>) {
    return config;
  }

  private _handleError(error: AxiosError): HttpError {
    if (!error.response) throw error;
    const data = error.response.data as any;
    const time = dayjs().format("YYYY-MM-DD HH:mm:ss");
    throw HttpError.fromStatus(data.resultCode, time, data.resultMessage);
  }

  private _handleErrorMessage(resultCode: number): string {
    switch (resultCode) {
      case 400000404:
        return "예치금이 부족합니다.";
      case 401000022:
        return "고객님은 휴면계정입니다. 고객센터로 문의바랍니다.";
      case 500000001:
        return "데이터 저장에 실패하였습니다.";
      case 401000011:
        return "동일한 비밀번호로 변경할 수 없습니다.";
      case 500300001:
        return "메일 발송에 실패하였습니다. 다시 시도해 주세요.";
      case 401000012:
        return "비밀번호가 일치하지 않습니다.";
      case 403000102:
      case 403000202:
        return "사용자 인증이 만료되었습니다.";
      case 408000000:
      case 408100001:
        return "서버 응답이 없습니다. 다시 시도해 주세요.";
      /** INTERNAL_SERVER_ERROR */
      case 500000000:
      case 500000002:
      case 500000004:
      case 500100001:
      case 500100002:
      case 500200001:
      case 500200002:
      case 500400101:
      case 500400201:
        return "알 수 없는 오류가 발생하였습니다.";
      case 401000010:
        return "아이디와 비밀번호가 일치하지 않습니다.";
      /** BAD_REQUEST */
      case 400000000:
      case 400000001:
      case 400000002:
      case 400000003:
      case 400000004:
      case 400000102:
      case 400000103:
      case 400000200:
      case 400000201:
      case 400000202:
      case 400000203:
      case 400000204:
      case 400000301:
      case 400000302:
      case 400000401:
      case 400000402:
      case 400000403:
      case 400100001:
      case 400100002:
      case 400100003:
      case 400100004:
      case 400100005:
      case 400100006:
      case 400100007:
      case 400200001:
      case 400200002:
      case 401000000:
        // case 403000000:
        return "예기치 못한 오류가 발생하였습니다.";
      case 403000001:
        return "예기치 못한 오류로 삭제에 실패하였습니다.";
      case 403000002:
        return "예기치 못한 오류로 업데이트에 실패하였습니다.";
      case 403000101:
        return "예기치 못한 오류로 요청하신 작업을 수행할 수 없습니다.";
      case 408000102:
        return "예약취소기간 만료로 인해 취소 하실수 없습니다.";
      case 429000102:
        return "예약하실 수 없습니다.";
      case 403000201:
        return "요청하신 작업을 수행할 수 없습니다.";
      /** NOT_FOUND */
      case 404000000:
      case 404000001:
      case 404000002:
      case 404000101:
      case 404000102:
      case 404000103:
      case 404000104:
      case 404000105:
      case 404000106:
      case 404000107:
      case 404000108:
      case 404000109:
      case 404000110:
        return "요청한 결과를 찾을 수 없습니다.";
      // case 408000001:
      case 408000101:
      case 409000000:
      case 409000001:
      case 409000002:
      case 409000003:
        return "요청한 결과를 처리할 수 없습니다.";
      case 429000000:
        return "요청 횟수를 초과하였습니다.";
      case 409000004:
        return "이미 가입된 이메일 입니다.";
      case 409000005:
      case 409000007:
        return "이미 가입된 전화번호 입니다.";
      case 408000103:
        return "이미 탑승처리된 예약입니다.";
      case 429000103:
        return "인증번호 발급 횟수를 초과하였습니다.";
      case 401000023:
        return "담당자 승인 후 로그인 하실 수 있습니다.";
      case 401000203:
        return "로그인 가능 횟수를 초과하였습니다. 본인인증 후 이용 가능합니다.";
      case 401000300:
      case 401000301:
      case 401000311:
        return "인증번호가 일치하지 않습니다.";
      case 401000302:
      case 401000312:
        return "인증번호를 입력해 주세요.";
      case 401000303:
        return "인증시간이 만료되었습니다.";
      case 401000021:
        return "입력하신 정보와 일치하는 계정이 없습니다.";
      case 409000006:
        return "현재 가입할 수 없습니다.";
      case 500300002:
        return "SMS 발송에 실패하였습니다. 다시 시도해 주세요.";
      default:
        break;
    }
    return "";
  }

  private _setErrorStatus(error: AxiosError) {
    if (!error.response) throw error;

    const data = error.response.data as any;
    const resultCode = data.resultCode;
    const resultTs = data?.resultTs ?? dayjs();
    let errorMessage = this._handleErrorMessage(resultCode);
    if (errorMessage.length <= 0) {
      errorMessage = data.resultMessage;
    }
    throw HttpError.fromStatus(resultCode, resultTs, errorMessage, data.data);
  }

  updateToken = async () => {
    const refreshToken = useGetRefreshToken();
    const memberUid = window.localStorage.getItem(MEMBER_ID) ?? "";

    const url = `${useGetProtocolHost()}/aibos/client/api/${
      process.env.VUE_APP_API_VERSION
    }/public/service/${SERVICE_UID}/member/refresh-token`;
    const data = qs.stringify({ memberUid, refreshToken });

    let isSuccess = false;
    await this.axiosService
      .post(url, data, {
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
      })
      .then((response) => {
        const data = response.data.data;
        useSetTokens(data.accessToken, data.refreshToken);
        isSuccess = true;

        if (data.needPasswordChangeYn) {
          router.replace({ name: RouterName.ChangeExpiredPassword });
        }
      })
      .catch((error) => {
        this._setErrorStatus(error);
      });
    return isSuccess;
  };

  _handleResponseError = async (error: AxiosError) => {
    const data = error.response?.data as any;
    const resultCode = data.resultCode;
    switch (resultCode) {
      case 401000100: // Invalid Access Token
      case 401000101: {
        // Access Token Expired
        if (this.retryTokenRefreshCount >= this.retryTokenRefreshLimit) {
          console.error(
            `Reached retry token limit of ${this.retryTokenRefreshLimit}`
          );
          break;
        }
        this.retryTokenRefreshCount += 1;
        const result = await this.updateToken();
        if (!result) return;

        const newAccessToken = useGetAccessToken();
        const requestConfig = error.config;
        if (requestConfig?.headers) {
          requestConfig.headers!.Authorization = `Bearer ${newAccessToken}`;
        }

        try {
          const response = await this.axiosService.request(requestConfig || {});
          this.retryTokenRefreshCount = 0;
          return response;
        } catch (retryError) {
          console.error(
            `An error occurred while retrying the request '${requestConfig}': ${retryError}`
          );
          break;
        }
      }
      case 401000200: // Invalid Refresh Token
      case 401000201: {
        // Refresh Token Not Found
        useRemoveTokens();

        router.replace({
          name: RouterName.Login,
          params: { isInvalidLogin: "true" },
        });
        break;
      }
      case 401000202: {
        // 비밀번호 유효기간이 지난 경우
        router.replace({
          name: RouterName.ChangeExpiredPassword,
        });
        break;
      }
      default:
        break;
    }
    this._setErrorStatus(error);
  };
}
